import React, {useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import Styled from 'styled-components';
import {Alert, Checkbox, DatePicker, FlexColumn, FlexRow, Label, Modal, Text, TextArea} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {createToast} from '../../../shared/components/toasts/toast';
import {usePortalEligibleApprovers} from '../../../shared/hooks/usePortalEligibleApprovers';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import PortalApproverPicker from '../../../shared/components/pickers/PortalApproverPicker';
import {uploadClientAssetFile, type UploadRef} from './uploadClientAssetFile';
import {commitAsync, CreateMutation, requestTicket} from './clientAssetMutations';
import type {ClientAssetCreateType, ClientAssetTask} from './ClientAssetsPage';
import type {CreateClientAssetModalDefaultApproverQuery} from './__generated__/CreateClientAssetModalDefaultApproverQuery.graphql';

export type CreateClientAssetModalPayload = {
	assetType: ClientAssetCreateType;
	/** Relay global id of the project. */
	projectId: string;
	tasks: ClientAssetTask[];
	/** Re-runs the page query after a successful create. */
	onCreated: () => void;
};

type Props = CreateClientAssetModalPayload & {closeModal: () => void};

type ClosureMode = 'auto' | 'acknowledge';

/** Which optional fields each type shows; every type has a name, a note and a task. */
const FIELDS: Record<
	ClientAssetCreateType,
	{file: boolean; due: boolean; readAck: boolean; request: boolean; approver: boolean}
> = {
	deliverable: {file: true, due: true, readAck: false, request: false, approver: true},
	reference: {file: true, due: false, readAck: true, request: false, approver: false},
	input_request: {file: false, due: true, readAck: false, request: true, approver: false},
};

// FOR-38: the deliverable approver to pre-fill for the chosen task (its sign-off approver, else
// its phase's), or null. project(internalId:) because $projectId is the Relay global id, as in
// usePortalEligibleApprovers. An option-filling fetch, so useForecastFetchQuery is the sanctioned hook.
const defaultApproverQuery = graphql`
	query CreateClientAssetModalDefaultApproverQuery($projectId: ID!, $taskId: ID) {
		viewer {
			project(internalId: $projectId) {
				clientAssetDefaultApprover(taskId: $taskId)
			}
		}
	}
`;

const UPLOAD_ERROR_MESSAGE_IDS: Record<string, string> = {
	FILE_TOO_LARGE: 'client_assets.field.file_too_large',
	UPLOAD_FAILED: 'client_assets.error.upload_failed',
};
const TRANSLATION_ID = /^[a-z0-9_]+(\.[a-z0-9_]+)+$/;

/**
 * The inline message for a failed submit, or null when there is nothing to add: UNHANDLED
 * means the framework's mutation error modal has already explained it.
 */
const errorMessageIdFor = (error: unknown): string | null => {
	const message = error instanceof Error ? error.message : '';
	if (UPLOAD_ERROR_MESSAGE_IDS[message]) return UPLOAD_ERROR_MESSAGE_IDS[message];
	// commitAsync rejects a recognised workspace refusal with its translation id.
	return TRANSLATION_ID.test(message) ? message : null;
};

const NativeSelect = Styled.select`
	height: 32px;
	padding: 0 8px;
	border: 1px solid #c9c9d9;
	border-radius: 4px;
	background: #fff;
	font-size: 13px;
`;

const RadioLabel = Styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	cursor: pointer;
`;

/**
 * One create flow for the three firm-authored asset types. A file-bearing type is uploaded
 * browser → S3 through a presigned PUT first, and the asset is created from the returned
 * upload ref, which the server re-verifies; an input request never uploads.
 */
export const CreateClientAssetModal = ({assetType, projectId, tasks, onCreated, closeModal}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const t = (id: string) => formatMessage({id});
	const fields = FIELDS[assetType];
	// One fetch serves both pickers: "Send to" (input request) and "Approver" (deliverable). Since
	// FOR-38 T7 it is served to anyone with full access to the project, so [] means nobody at the
	// client is eligible yet. "Anyone at the client" stays the Send-to default.
	const eligible = usePortalEligibleApprovers(fields.request || fields.approver ? projectId : null);
	const {approvers} = eligible;

	const [file, setFile] = useState<File | null>(null);
	const [name, setName] = useState('');
	const [note, setNote] = useState('');
	const [taskId, setTaskId] = useState('');
	const [dueDate, setDueDate] = useState<string | undefined>(undefined);
	const [requiresReadAck, setRequiresReadAck] = useState(false);
	const [sendTo, setSendTo] = useState('');
	const [closureMode, setClosureMode] = useState<ClosureMode>('auto');
	const [submitting, setSubmitting] = useState(false);
	const [uploading, setUploading] = useState(false);
	// A refusal renders inline so the composed asset survives; the shared warning modal
	// would replace this one.
	const [errorMessageId, setErrorMessageId] = useState<string | null>(null);
	// State alone cannot stop a second click landing before the disabled render commits.
	const submittingRef = useRef(false);

	// FOR-38: the deliverable's approver. Pre-filled from the chosen task's default, which is
	// re-fetched on every task change; an answer for a task that is no longer selected is dropped,
	// and a manual pick survives a task with no default.
	const [approverId, setApproverId] = useState<string | null>(null);
	const [defaultApproverId, setDefaultApproverId] = useState<string | null>(null);
	const {fetch: fetchDefaultApprover} = useForecastFetchQuery<
		CreateClientAssetModalDefaultApproverQuery['response'],
		{projectId: string; taskId: string}
	>(defaultApproverQuery);
	const selectedTaskRef = useRef(taskId);

	useEffect(() => {
		selectedTaskRef.current = taskId;
		setDefaultApproverId(null);
		if (!fields.approver || !taskId) return;
		// No onError: a failed lookup only means no pre-fill; the PM still picks.
		fetchDefaultApprover({projectId, taskId}, data => {
			if (selectedTaskRef.current !== taskId) return;
			const id = data?.viewer?.project?.clientAssetDefaultApprover ?? null;
			setDefaultApproverId(id);
			if (id) setApproverId(id);
		});
	}, [fields.approver, projectId, taskId]);

	// Only an offered approver counts: a stale default or a lost eligibility reads as no choice.
	const approverValue = approverId !== null && approvers.some(a => a.portalUserId === approverId) ? approverId : null;
	// Nothing to pick from (nobody eligible, or the list failed): the create goes with null and the
	// workspace applies the task default itself (T4) or leaves it for Assign in the drawer.
	const noApprovers = !eligible.loading && approvers.length === 0;
	const approverReady = !fields.approver || approverValue !== null || noApprovers;
	const defaultApproverEmail =
		defaultApproverId !== null && approverValue === defaultApproverId
			? approvers.find(a => a.portalUserId === defaultApproverId)?.email ?? null
			: null;

	const trimmedName = name.trim();
	const canSubmit = !!trimmedName && (!fields.file || !!file) && approverReady && !submitting;

	const handleSubmit = async () => {
		if (!canSubmit || submittingRef.current) return;
		submittingRef.current = true;
		setSubmitting(true);
		setErrorMessageId(null);
		try {
			let upload: UploadRef | null = null;
			if (fields.file && file) {
				setUploading(true);
				upload = await uploadClientAssetFile({projectId, file, requestTicket});
				setUploading(false);
			}
			await commitAsync(CreateMutation, {
				projectId,
				assetType,
				name: trimmedName,
				note: note.trim() || null,
				taskId: taskId || null,
				dueDate: fields.due ? dueDate ?? null : null,
				requiresReadAck: fields.readAck ? requiresReadAck : false,
				requestedFromPortalUserId: fields.request ? sendTo || null : null,
				closureMode: fields.request ? closureMode : null,
				// Deliverables only: any other type never carries the key (the workspace would refuse it).
				...(fields.approver ? {approverPortalUserId: approverValue} : {}),
				...(upload ?? {}),
			});
			createToast({
				duration: 5000,
				variant: 'banner',
				message: (
					<Alert
						type={Alert.TYPE.SUCCESS}
						text={formatMessage({id: 'client_assets.created_toast'}, {name: trimmedName})}
					/>
				),
			});
			onCreated();
			closeModal();
		} catch (error) {
			submittingRef.current = false;
			setSubmitting(false);
			setUploading(false);
			setErrorMessageId(errorMessageIdFor(error));
		}
	};

	return (
		<Modal onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{t(`client_assets.modal.${assetType}.title`)}</Modal.Title>
			<Modal.Content data-cy="create-client-asset-modal">
				<FlexColumn gap="m">
					<Text color="medium">{t(`client_assets.modal.${assetType}.subtitle`)}</Text>
					{fields.file && (
						<FlexColumn gap="xs">
							<Label>{t('client_assets.field.file')}</Label>
							<input
								type="file"
								data-cy="create-client-asset-file"
								data-testid="create-client-asset-file"
								onChange={e => {
									setFile(e.target.files?.[0] ?? null);
									setErrorMessageId(null);
								}}
							/>
							<Text color="medium" size="1">
								{t('client_assets.field.file_hint')}
							</Text>
						</FlexColumn>
					)}
					<FlexColumn gap="xs">
						<Label>{t(fields.request ? 'client_assets.field.what' : 'client_assets.field.name')}</Label>
						<input
							type="text"
							value={name}
							maxLength={255}
							onChange={e => setName(e.target.value)}
							data-cy="create-client-asset-name"
							data-testid="create-client-asset-name"
						/>
					</FlexColumn>
					<TextArea
						label={t(fields.request ? 'client_assets.field.instructions' : 'client_assets.field.note')}
						isOptional
						value={note}
						// TextArea's onChange receives the VALUE, not an event.
						onChange={(value: string) => setNote(value)}
						// Required by TextArea's props; the value is already tracked on every change.
						onBlur={() => undefined}
						data-cy="create-client-asset-note"
					/>
					<FlexColumn gap="xs">
						<Label>{t('client_assets.field.task')}</Label>
						<NativeSelect
							value={taskId}
							onChange={e => setTaskId(e.target.value)}
							data-cy="create-client-asset-task"
							data-testid="create-client-asset-task"
						>
							<option value="">{t('client_assets.field.no_task')}</option>
							{tasks.map(task => (
								<option key={task.id} value={task.id}>
									{task.name}
								</option>
							))}
						</NativeSelect>
					</FlexColumn>
					{fields.approver && (
						<FlexColumn gap="xs">
							<Label>{t('client_assets.field.approver')}</Label>
							{noApprovers && !eligible.error ? (
								// The test hooks sit on the wrapper: DS Text forwards only data-cy.
								<div data-testid="create-client-asset-no-approvers" data-cy="create-client-asset-no-approvers">
									<Text color="medium">{t('client_assets.field.no_approvers')}</Text>
								</div>
							) : (
								// On a load failure the picker renders its own "couldn't load" line.
								<PortalApproverPicker
									value={approverValue}
									approvers={approvers}
									loading={eligible.loading}
									error={eligible.error}
									onChange={setApproverId}
									placeholder={t('client_assets.field.approver_placeholder')}
									ariaLabel={t('client_assets.field.approver')}
									dataCy="create-client-asset-approver"
								/>
							)}
							{defaultApproverEmail && (
								<div
									data-testid="create-client-asset-approver-default"
									data-cy="create-client-asset-approver-default"
								>
									<Text color="medium" size="1">
										{formatMessage(
											{id: 'client_assets.field.approver_default'},
											{name: defaultApproverEmail}
										)}
									</Text>
								</div>
							)}
						</FlexColumn>
					)}
					{fields.due && (
						<FlexColumn gap="xs">
							<Label>{t('client_assets.field.due')}</Label>
							<DatePicker
								value={dueDate}
								onChange={(dateString: string) => setDueDate(dateString || undefined)}
								aria-label={t('client_assets.field.due')}
								data-cy="create-client-asset-due"
							/>
						</FlexColumn>
					)}
					{fields.readAck && (
						<FlexRow gap="s" alignItems="center">
							<Checkbox
								checked={requiresReadAck}
								onChange={(checked: boolean) => setRequiresReadAck(checked)}
								aria-label={t('client_assets.field.read_ack')}
								data-cy="create-client-asset-read-ack"
							/>
							<Text>{t('client_assets.field.read_ack')}</Text>
						</FlexRow>
					)}
					{fields.request && (
						<>
							<FlexColumn gap="xs">
								<Label>{t('client_assets.field.send_to')}</Label>
								<NativeSelect
									value={sendTo}
									onChange={e => setSendTo(e.target.value)}
									data-cy="create-client-asset-send-to"
									data-testid="create-client-asset-send-to"
								>
									<option value="">{t('client_assets.field.anyone')}</option>
									{approvers.map(approver => (
										<option key={approver.portalUserId} value={approver.portalUserId}>
											{approver.email}
										</option>
									))}
								</NativeSelect>
							</FlexColumn>
							<FlexColumn gap="xs" role="radiogroup" aria-label={t('client_assets.field.closure')}>
								<Label>{t('client_assets.field.closure')}</Label>
								{(['auto', 'acknowledge'] as ClosureMode[]).map(mode => (
									<RadioLabel key={mode}>
										<input
											type="radio"
											name="create-client-asset-closure"
											value={mode}
											checked={closureMode === mode}
											onChange={() => setClosureMode(mode)}
											data-cy={`create-client-asset-closure-${mode}`}
										/>
										{t(`client_assets.closure.${mode}`)}
									</RadioLabel>
								))}
							</FlexColumn>
						</>
					)}
					{errorMessageId && (
						<div data-testid="create-client-asset-error" data-cy="create-client-asset-error">
							<Alert type={Alert.TYPE.ERROR} text={t(errorMessageId)} />
						</div>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="create-client-asset-cancel">
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton onPress={handleSubmit} disabled={!canSubmit} dataCy="create-client-asset-submit">
					{t(uploading ? 'client_assets.uploading' : `client_assets.submit.${assetType}`)}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const showCreateClientAssetModal = (payload: CreateClientAssetModalPayload): void => {
	showModal({type: MODAL_TYPE.CREATE_CLIENT_ASSET, ...payload});
};
